import { loader } from '../map_state/loader'
import { state } from '../map_state/state'
import { viewport } from '../map_state/viewport'
import { Renderer } from '../renderer'
import { callMethodOnKeydown, isTyping } from '../helpers'
import { pathcalculatorTool } from './path_calculator_tool'
import { historyTool } from './history_tool'
import { Sensor } from '../models/sensor'
import { addSensorTool } from './add_sensor_tool'
import { groupTool } from './group_tool'
import { linearFlowTool } from './linear_flow_tool'
import { edgeDistanceTool } from './edge_distance_tool'
import { pointDistanceTool } from './point_distance_tool'
import { zoomTool } from './zoom_tool'
import { darkModeTool } from './dark_mode_tools'
import { panTool } from './pan_tool'
import { cropTool } from './crop_tool'
import { dragTool } from './drag_tool'
import { pathTracerTool } from './path_tracer_tool'

class ImportExportTool {
  constructor () {
    this.element = document.getElementById('mct-import-map')
    this.field = document.getElementById('upload-map')
    this._lastObjectUrl = null
    this.bindEvents()
  }

  bindEvents () {
    this.runAction = this.runAction.bind(this)
    this.onFileUpload = this.onFileUpload.bind(this)
    this.onKeyDown = this.onKeyDown.bind(this)

    this.element.addEventListener('click', this.runAction)
    this.field.addEventListener('change', this.onFileUpload)
    document.addEventListener('keydown', this.onKeyDown)
  }

  unBindEvents () {
    this.element.removeEventListener('click', this.runAction)
    this.field.removeEventListener('change', this.onFileUpload)
    document.removeEventListener('keydown', this.onKeyDown)

    if (this._lastObjectUrl) {
      URL.revokeObjectURL(this._lastObjectUrl)
      this._lastObjectUrl = null
    }
  }

  runAction () {
    if (this.element.id === 'mct-import-map') this.import()
    else this.export()
  }

  import () {
    state.swapMode = false
    this.field.click()
  }

  export () {
    if (!state.mapImage) {
      loader.setStatusMessage('failure', 'Upload a map first')
      return
    }

    const extension = this.getExportExtension()

    if (extension === 'PNG') {
      this.exportPNG()
      if (this.shouldIncludeJsonWithExport()) this.exportJSON()
      return
    }

    if (extension === 'SVG') {
      this.exportSVG()
      if (this.shouldIncludeJsonWithExport()) this.exportJSON()
      return
    }

    this.exportJSON()
  }

  change () {
    if (this.element.id === 'mct-import-map') {
      this.element.setAttribute('id', 'mct-export-map')
      this.element.setAttribute('data-tooltip-text', 'Export Map')
      this.element.classList.replace('mct-import-map', 'mct-export-map')
    } else {
      this.element.setAttribute('id', 'mct-import-map')
      this.element.setAttribute('data-tooltip-text', 'Import Map')
      this.element.classList.replace('mct-export-map', 'mct-import-map')
    }
  }

  onKeyDown (e) {
    if (isTyping(e.target)) return
    if (e.key.toLowerCase() === 'i') callMethodOnKeydown(e.target, this.runAction)
  }

  getFilename () {
    const input = document.getElementById('mct-filename')
    const raw = (input?.value || '').trim()
    if (!raw) return 'map'

    return raw
      .replace(/[\u0000-\u001F\u007F]/g, '')
      .replace(/[\\/:*?"<>|]/g, '-')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/[. ]+$/g, '') || 'map'
  }

  getExportExtension () {
    const select = document.getElementById('mct-export-extension')
    return String(select?.value || 'PNG').toUpperCase()
  }

  _findSettingsCheckboxByText (text) {
    const rows = Array.from(document.querySelectorAll('#mct-settings-menu .mct-setting'))
    return rows.find(row => row.textContent.toLowerCase().includes(text.toLowerCase()))
      ?.querySelector('input[type="checkbox"]') || null
  }

  shouldHideZoneLabels () {
    const checkbox = this._findSettingsCheckboxByText('export without zone labels')
    return !!checkbox?.checked
  }

  shouldIncludeJsonWithExport () {
    const checkbox = document.getElementById('mct-include-json-with-export')
    return !!checkbox?.checked
  }

  syncVisibilityCheckboxes () {
    const setChecked = (name, value) => {
      const checkbox = document.querySelector(`#mct-settings-menu input[name="${name}"]`)
      if (checkbox) checkbox.checked = !!value
    }

    setChecked('visibleSensors', state.visibleSensors)
    setChecked('visiblePointDistances', state.visiblePointDistances)
    setChecked('visibleLinearFlows', state.visibleLinearFlows)
    setChecked('visibleLables', state.visibleLables)
  }

  deactivateInteractiveModes () {
    if (state.panMode) panTool.activate(false)
    if (state.cropMode) cropTool.activate(false)
    if (state.edgeDistanceMode) edgeDistanceTool.activate(false)
    if (state.pointDistanceMode) pointDistanceTool.activate(false)
    if (state.addSensorMode) addSensorTool.activate(false)
    if (state.linearFlowMode) linearFlowTool.activate(false)
    if (state.dragMode) dragTool.activate(false)
    if (state.groupMode) groupTool.activate(false)
    if (state.tracerMode) pathTracerTool.enableMode(state.tracerMode, false)

    state.swapMode = false
    state.flowStartSensorId = null
    state.flowPreviewWorld = null
    state.previewSensor = null
    state.zoneDrag = null
    state.labelsOccupied = []
    state.drag = null
    state.cropRect = { x: 0, y: 0, w: 0, h: 0 }
  }

  clampValue (value, min, max) {
    return Math.max(min, Math.min(max, value))
  }

  clampPointToImageEdge (point, mapWorld, imageWidth, imageHeight) {
    if (!point) return

    point.x = this.clampValue(point.x, mapWorld.x, mapWorld.x + imageWidth)
    point.y = this.clampValue(point.y, mapWorld.y, mapWorld.y + imageHeight)
  }

  clampEverythingToSwappedMap (mapWorld, imageWidth, imageHeight) {
    state.sensors.forEach(sensor => {
      this.clampPointToImageEdge(sensor.position, mapWorld, imageWidth, imageHeight)
    })

    if (state.previewSensor?.position) this.clampPointToImageEdge(state.previewSensor.position, mapWorld, imageWidth, imageHeight)
    if (state.cursorWorld) this.clampPointToImageEdge(state.cursorWorld, mapWorld, imageWidth, imageHeight)
    if (state.flowPreviewWorld) this.clampPointToImageEdge(state.flowPreviewWorld, mapWorld, imageWidth, imageHeight)
    if (state.startPoint) this.clampPointToImageEdge(state.startPoint, mapWorld, imageWidth, imageHeight)

    state.tracerPointsWorld.forEach(point => this.clampPointToImageEdge(point, mapWorld, imageWidth, imageHeight))
    state.tracerPathWorld.forEach(point => this.clampPointToImageEdge(point, mapWorld, imageWidth, imageHeight))

    state.segments.forEach(segment => {
      if (segment.a) this.clampPointToImageEdge(segment.a, mapWorld, imageWidth, imageHeight)
      if (segment.b) this.clampPointToImageEdge(segment.b, mapWorld, imageWidth, imageHeight)
    })
  }

  syncUiAfterRawImport () {
    addSensorTool.rebuildTab()
    groupTool.syncUi()
    pathcalculatorTool.resetDimensions()
    this.syncVisibilityCheckboxes()
    zoomTool.setZoom(state.zoomValue)
    viewport.scrollToCenter()
    darkModeTool.syncUi()
    Renderer.render()
  }

  syncUiAfterProjectLoad () {
    addSensorTool.rebuildTab()
    groupTool.syncUi()

    if (state.calibration) pathcalculatorTool.updateFields()
    else pathcalculatorTool.resetDimensions()

    this.syncVisibilityCheckboxes()
    zoomTool.setZoom(state.zoomValue)
    viewport.scrollToCenter()
    darkModeTool.syncUi()
    Renderer.render()
  }

  applyImportedImage (img) {
    this.deactivateInteractiveModes()
    state.reset()

    state.mapImage = img
    this.setOriginalMap(img)
    state.mapWorld = {
      x: (viewport.spacer.offsetWidth - img.naturalWidth) / 2,
      y: (viewport.spacer.offsetHeight - img.naturalHeight) / 2
    }
    state.canEditMap = true

    if (this.element.id === 'mct-import-map') this.change()
    this.syncUiAfterRawImport()
  }

  applySwappedImage (img) {
    const oldMapWorld = { ...state.mapWorld }

    this.deactivateInteractiveModes()

    state.mapImage = img
    this.setOriginalMap(img)
    state.mapWorld = oldMapWorld
    state.canEditMap = true

    state.tracerPointsWorld = []
    state.tracerPathWorld = []
    state.lenPath = null
    state.lenUnit = null
    state.calibration = false
    state.boundaryInfo = null
    state.cursorWorld = null
    state.segments = []
    state.startPoint = null
    state.labelsOccupied = []
    state.flowStartSensorId = null
    state.flowPreviewWorld = null

    this.clampEverythingToSwappedMap(oldMapWorld, img.naturalWidth, img.naturalHeight)
    pathcalculatorTool.resetDimensions()
    for (const s of state.sensors) {
      s.resetZoneFields()
    }
    addSensorTool.rebuildTab()
    groupTool.syncUi()
    Renderer.render()
  }

  async onFileUpload () {
    const file = this.field.files?.[0]
    if (!file) {
      loader.setStatusMessage('failure', 'No file uploaded')
      return
    }

    const name = (file.name || '').toLowerCase()
    const ext = name.includes('.') ? name.split('.').pop() : ''
    const isJson = file.type === 'application/json' || ext === 'json'
    const isSvg = file.type === 'image/svg+xml' || ext === 'svg'
    const isImage = file.type.startsWith('image/') || ['png', 'jpg', 'jpeg', 'webp'].includes(ext)

    let success = false

    loader.setStatusMessage('processing', state.swapMode ? 'Swapping map...' : 'Uploading map...')

    try {
      if (isJson) {
        if (state.swapMode) {
          loader.setStatusMessage('failure', 'JSON import is not supported while replacing the map')
          return
        }
        await this.handleProjectJson(file)
      } else if (isSvg) {
        await this.handleSvg(file)
      } else if (isImage) {
        await this.handleRasterImage(file)
      } else {
        loader.setStatusMessage('failure', 'Unsupported file')
        return
      }

      success = true
    } catch (error) {
      console.error(error)
      loader.setStatusMessage('failure', 'Import failed')
    } finally {
      this.field.value = ''
      state.swapMode = false

      if (success) {
        historyTool.add()
        historyTool.toggleVisibility()
        Renderer.render()
      }
    }
  }

  async handleRasterImage (file) {
    if (this._lastObjectUrl) {
      URL.revokeObjectURL(this._lastObjectUrl)
      this._lastObjectUrl = null
    }

    const url = URL.createObjectURL(file)
    this._lastObjectUrl = url

    const img = new Image()
    await new Promise((resolve, reject) => {
      img.onload = resolve
      img.onerror = reject
      img.src = url
    })

    URL.revokeObjectURL(url)
    if (this._lastObjectUrl === url) this._lastObjectUrl = null

    if (state.swapMode && state.mapImage) this.applySwappedImage(img)
    else this.applyImportedImage(img)
  }

  async handleSvg (file) {
    const svgText = await file.text()
    const hasXmlns = /<svg[^>]+xmlns=/.test(svgText)
    const safeSvg = hasXmlns ? svgText : svgText.replace('<svg', '<svg xmlns="http://www.w3.org/2000/svg"')

    const blob = new Blob([safeSvg], { type: 'image/svg+xml' })
    const url = URL.createObjectURL(blob)

    const svgImg = new Image()
    svgImg.decoding = 'async'
    await new Promise((resolve, reject) => {
      svgImg.onload = resolve
      svgImg.onerror = reject
      svgImg.src = url
    })

    const w = svgImg.naturalWidth || 2000
    const h = svgImg.naturalHeight || 2000
    const canvas = document.createElement('canvas')
    canvas.width = w
    canvas.height = h

    const ctx = canvas.getContext('2d')
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, w, h)
    ctx.drawImage(svgImg, 0, 0, w, h)

    const pngUrl = canvas.toDataURL('image/png')
    const img = new Image()
    img.decoding = 'async'
    await new Promise((resolve, reject) => {
      img.onload = resolve
      img.onerror = reject
      img.src = pngUrl
    })

    URL.revokeObjectURL(url)

    if (state.swapMode && state.mapImage) this.applySwappedImage(img)
    else this.applyImportedImage(img)
  }

  async handleProjectJson (file) {
    const text = await file.text()

    let project
    try {
      project = JSON.parse(text)
    } catch {
      loader.setStatusMessage('failure', 'Invalid JSON')
      return
    }

    await this.loadProjectJson(project)
  }

  async loadProjectJson (project) {
    if (!project?.map?.dataUrl) {
      loader.setStatusMessage('failure', 'Missing map dataUrl')
      return
    }

    if (project?.version === 1) {
      loader.setStatusMessage('failure', 'Map Creator V1 JSON file is not supported.')
      return
    }

    const img = await this.loadImageFromDataUrl(project.map.dataUrl)
    const origMapDataUrl = project.origMap?.dataUrl || project.map?.dataUrl
    const origImg = await this.loadImageFromDataUrl(origMapDataUrl)

    this.deactivateInteractiveModes()
    state.reset()
    state.mapImage = img
    this.setOriginalMap(origImg || img)

    const saved = project.state || {}

    state.mapWorld = {
      x: Number.isFinite(saved.mapWorld?.x)
        ? saved.mapWorld.x
        : (viewport.spacer.offsetWidth - img.naturalWidth) / 2,
      y: Number.isFinite(saved.mapWorld?.y)
        ? saved.mapWorld.y
        : (viewport.spacer.offsetHeight - img.naturalHeight) / 2
    }

    state.canEditMap =
      typeof saved.canEditMap === 'boolean'
        ? saved.canEditMap
        : (Array.isArray(saved.sensors) ? saved.sensors.length === 0 : true)

    state.zoomValue = Number.isFinite(saved.zoomValue) ? saved.zoomValue : 1
    state.darkMode = !!saved.darkMode

    const importedLenPath = Number(saved.lenPath)
    state.lenPath = Number.isFinite(importedLenPath) && importedLenPath > 0 ? importedLenPath : null
    state.lenUnit = typeof saved.lenUnit === 'string' && saved.lenUnit ? saved.lenUnit : null
    state.calibration = Number.isFinite(state.lenPath) && state.lenPath > 0 && !!state.lenUnit

    state.groupSensorIds = Array.isArray(saved.groupSensorIds)
      ? saved.groupSensorIds.map(Number)
      : []

    state.groups = Array.isArray(saved.groups)
      ? saved.groups.map(group => ({
        id: Number(group.id),
        name: group.name ?? '',
        sensorIds: Array.isArray(group.sensorIds) ? group.sensorIds.map(Number) : []
      }))
      : []

    state.nextGroupId = Number.isFinite(saved.nextGroupId)
      ? saved.nextGroupId
      : Math.max(0, ...state.groups.map(group => Number(group.id) || 0)) + 1

    state.visibleSensors = saved.visibleSensors ?? true
    state.visiblePointDistances = saved.visiblePointDistances ?? true
    state.visibleLinearFlows = saved.visibleLinearFlows ?? true
    state.visibleLables = saved.visibleLables ?? true

    state.segments = Array.isArray(saved.segments)
      ? saved.segments.map(segment => ({
        a: segment?.a ? { ...segment.a } : null,
        b: segment?.b ? { ...segment.b } : null
      }))
      : []

    state.sensors = Array.isArray(saved.sensors)
      ? saved.sensors.map(raw => new Sensor({
        id: raw.id,
        name: raw.name ?? '',
        position: raw.position && Number.isFinite(Number(raw.position.x)) && Number.isFinite(Number(raw.position.y))
          ? { x: Number(raw.position.x), y: Number(raw.position.y) }
          : { x: 0, y: 0 },
        flowEndSensorIds: Array.isArray(raw.flowEndSensorIds)
          ? raw.flowEndSensorIds.map(Number)
          : [],
        edgeFadePx: Number.isFinite(Number(raw.edgeFadePx)) ? Number(raw.edgeFadePx) : undefined,
        zoneAColor: raw.zoneAColor,
        zoneBColor: raw.zoneBColor,
        sensorColor: raw.sensorColor,
        enableZoneA: raw.enableZoneA ?? true,
        enableZoneB: raw.enableZoneB ?? true,
        visible: raw.visible ?? true,
        engagedDuration: raw.engagedDuration ?? 60,
        rssiA: Number.isFinite(Number(raw.rssiA)) ? Math.round(Number(raw.rssiA)) : undefined,
        rssiB: Number.isFinite(Number(raw.rssiB)) ? Math.round(Number(raw.rssiB)) : undefined,
        zoneARadius: Number.isFinite(Number(raw.zoneARadius)) ? Number(raw.zoneARadius) : undefined,
        zoneBRadius: Number.isFinite(Number(raw.zoneBRadius)) ? Number(raw.zoneBRadius) : undefined,
        zoneRadiusUnit: typeof raw.zoneRadiusUnit === 'string' ? raw.zoneRadiusUnit : undefined,
        maxOuterRadius: Number.isFinite(Number(raw.maxOuterRadius)) ? Number(raw.maxOuterRadius) : undefined,
        defaultInnerRadius: Number.isFinite(Number(raw.defaultInnerRadius)) ? Number(raw.defaultInnerRadius) : undefined,
        defaultOuterRadius: Number.isFinite(Number(raw.defaultOuterRadius)) ? Number(raw.defaultOuterRadius) : undefined
      }))
      : []

    for (const s of state.sensors) {
      s.ensureDefaults(true)
      s.enableRanges()
    }

    state.nextSensorId = Number.isFinite(saved.nextSensorId)
      ? saved.nextSensorId
      : Math.max(0, ...state.sensors.map(sensor => Number(sensor.id) || 0)) + 1

    state.sensorNameChangedOnce = state.sensors.some((sensor, index) => sensor.name !== `Sensor ${index + 1}`)
    state.normalizeGroupState()

    if (this.element.id === 'mct-import-map') this.change()
    this.syncUiAfterProjectLoad()
  }

  _imageToDataUrl (img) {
    if (typeof img?.src === 'string' && img.src.startsWith('data:image/')) return img.src

    const canvas = document.createElement('canvas')
    canvas.width = img.naturalWidth || img.width
    canvas.height = img.naturalHeight || img.height
    canvas.getContext('2d').drawImage(img, 0, 0)
    return canvas.toDataURL('image/png')
  }

  _downloadTextFile (filename, text, mime = 'application/octet-stream') {
    const blob = new Blob([text], { type: mime })
    const url = URL.createObjectURL(blob)

    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()

    setTimeout(() => URL.revokeObjectURL(url), 0)
  }

  _downloadCanvasPng (filename, canvas) {
    const a = document.createElement('a')
    a.href = canvas.toDataURL('image/png')
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
  }

  _drawExportLegend (ctx, width, mapHeight, legendHeight) {
    const items = [
      { color: Sensor.DEFAULT_ZONE_A_COLOR, label: 'Engagement zone' },
      { color: Sensor.DEFAULT_ZONE_B_COLOR, label: 'Passing zone' }
    ]
    const isStacked = width < 420
    const fontSize = Math.max(10, Math.min(16, Math.floor(width / 30)))
    const dotRadius = Math.max(5, Math.round(fontSize * 0.45))
    const dotGap = Math.max(8, Math.round(fontSize * 0.6))

    ctx.save()
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, mapHeight, width, legendHeight)
    ctx.strokeStyle = 'rgba(0,0,0,0.15)'
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.moveTo(0, mapHeight + 0.5)
    ctx.lineTo(width, mapHeight + 0.5)
    ctx.stroke()

    ctx.font = `${fontSize}px Arial, sans-serif`
    ctx.textAlign = 'left'
    ctx.textBaseline = 'middle'

    const itemWidths = items.map(item => (dotRadius * 2) + dotGap + ctx.measureText(item.label).width)
    const itemGap = Math.max(24, fontSize * 2)
    const totalWidth = itemWidths.reduce((sum, itemWidth) => sum + itemWidth, 0) + itemGap

    const drawItem = (item, x, y) => {
      ctx.beginPath()
      ctx.arc(x + dotRadius, y, dotRadius, 0, Math.PI * 2)
      ctx.fillStyle = item.color
      ctx.fill()
      ctx.fillStyle = '#111111'
      ctx.fillText(item.label, x + (dotRadius * 2) + dotGap, y)
    }

    if (!isStacked && totalWidth <= width - 24) {
      let x = (width - totalWidth) / 2
      const y = mapHeight + (legendHeight / 2)
      items.forEach((item, index) => {
        drawItem(item, x, y)
        x += itemWidths[index] + itemGap
      })
    } else {
      const lineHeight = legendHeight / items.length
      items.forEach((item, index) => {
        const x = Math.max(12, (width - itemWidths[index]) / 2)
        drawItem(item, x, mapHeight + (lineHeight * (index + 0.5)))
      })
    }

    ctx.restore()
  }

  _renderExportCanvas ({ showZoneLabels = true } = {}) {
    const img = state.mapImage
    const iw = img.naturalWidth
    const ih = img.naturalHeight
    const legendHeight = iw < 420 ? 88 : 64

    const canvas = document.createElement('canvas')
    canvas.width = iw
    canvas.height = ih + legendHeight
    const ctx = canvas.getContext('2d')
    ctx.drawImage(img, 0, 0, iw, ih)

    const camX = state.mapWorld.x
    const camY = state.mapWorld.y
    const bounds = { left: 0, top: 0, right: iw, bottom: ih }
    const target = Renderer.getRenderTarget({
      ctx,
      zoom: 1,
      labelsOccupied: [],
      worldToScreen: (x, y) => ({ x: x - camX, y: y - camY }),
      showZoneLabels
    })

    Renderer.drawMeasurePath(target)
    if (state.visiblePointDistances) Renderer.drawPointDistances(target)
    if (state.visibleLinearFlows) Renderer.drawLinearFlows(target)
    if (state.visibleSensors) Renderer.drawSensors(target, bounds)
    this._drawExportLegend(ctx, iw, ih, legendHeight)

    return canvas
  }

  exportPNG () {
    if (!state.mapImage) {
      loader.setStatusMessage('failure', 'Upload a map first')
      return
    }

    try {
      loader.setStatusMessage('processing', 'Exporting PNG...')
      const canvas = this._renderExportCanvas({
        showZoneLabels: !this.shouldHideZoneLabels()
      })
      this._downloadCanvasPng(`${this.getFilename()}.png`, canvas)
      loader.setStatusMessage('success', 'PNG exported')
    } catch (error) {
      console.error(error)
      loader.setStatusMessage('failure', 'PNG export failed')
    }
  }

  exportSVG () {
    if (!state.mapImage) {
      loader.setStatusMessage('failure', 'Upload a map first')
      return
    }

    try {
      loader.setStatusMessage('processing', 'Exporting SVG...')
      const canvas = this._renderExportCanvas({
        showZoneLabels: !this.shouldHideZoneLabels()
      })
      const dataUrl = canvas.toDataURL('image/png')
      const svg = `<?xml version="1.0" encoding="UTF-8"?>\n<svg xmlns="http://www.w3.org/2000/svg" width="${canvas.width}" height="${canvas.height}" viewBox="0 0 ${canvas.width} ${canvas.height}">\n  <image x="0" y="0" width="${canvas.width}" height="${canvas.height}" href="${dataUrl}" />\n</svg>`
      this._downloadTextFile(`${this.getFilename()}.svg`, svg, 'image/svg+xml')
      loader.setStatusMessage('success', 'SVG exported')
    } catch (error) {
      console.error(error)
      loader.setStatusMessage('failure', 'SVG export failed')
    }
  }

  exportJSON () {
    const img = state.mapImage
    if (!img) {
      loader.setStatusMessage('failure', 'Upload a map first')
      return
    }

    const origImg = state.origMap || img

    const project = {
      version: 2,
      savedAt: new Date().toISOString(),
      map: {
        dataUrl: this._imageToDataUrl(img),
        naturalWidth: img.naturalWidth,
        naturalHeight: img.naturalHeight
      },
      origMap: {
        dataUrl: this._imageToDataUrl(origImg),
        naturalWidth: origImg.naturalWidth,
        naturalHeight: origImg.naturalHeight
      },
      state: {
        darkMode: state.darkMode,
        mapWorld: { ...state.mapWorld },
        canEditMap: state.canEditMap,
        zoomValue: state.zoomValue,

        lenPath: state.lenPath,
        lenUnit: state.lenUnit,
        calibration: state.calibration,

        nextSensorId: state.nextSensorId,
        nextGroupId: state.nextGroupId,

        groupSensorIds: [...state.groupSensorIds],
        groups: state.groups.map(group => ({
          id: group.id,
          name: group.name ?? '',
          sensorIds: [...(group.sensorIds || [])]
        })),

        visibleSensors: state.visibleSensors,
        visiblePointDistances: state.visiblePointDistances,
        visibleLinearFlows: state.visibleLinearFlows,
        visibleLables: state.visibleLables,

        segments: state.segments.map(segment => ({
          a: segment.a ? { ...segment.a } : null,
          b: segment.b ? { ...segment.b } : null
        })),

        sensors: state.sensors.map(sensor => ({
          id: sensor.id,
          name: sensor.name,
          position: sensor.position ? { ...sensor.position } : { x: 0, y: 0 },
          flowEndSensorIds: [...(sensor.flowEndSensorIds || [])],
          edgeFadePx: sensor.edgeFadePx,
          zoneAColor: sensor.zoneAColor,
          zoneBColor: sensor.zoneBColor,
          sensorColor: sensor.sensorColor,
          enableZoneA: sensor.enableZoneA,
          enableZoneB: sensor.enableZoneB,
          visible: sensor.visible,
          engagedDuration: sensor.engagedDuration,
          rssiA: sensor.rssiA,
          rssiB: sensor.rssiB,
          zoneARadius: sensor.zoneARadius,
          zoneBRadius: sensor.zoneBRadius,
          zoneRadiusUnit: sensor.zoneRadiusUnit,
          maxOuterRadius: sensor.maxOuterRadius,
          defaultInnerRadius: sensor.defaultInnerRadius,
          defaultOuterRadius: sensor.defaultOuterRadius
        }))
      }
    }

    this._downloadTextFile(
      `${this.getFilename()}.mct.json`,
      JSON.stringify(project, null, 2),
      'application/json'
    )

    loader.setStatusMessage('success', 'JSON exported')
  }

  async loadImageFromDataUrl (dataUrl) {
    if (!dataUrl) return null

    const img = new Image()
    await new Promise((resolve, reject) => {
      img.onload = resolve
      img.onerror = reject
      img.src = dataUrl
    })

    return img
  }

  setOriginalMap (img) {
    state.origMap = img || null
  }
}

export let importExportTool = null

export function initImportExportTool () {
  importExportTool = new ImportExportTool()
  return importExportTool
}
